// The connections directory (Connections "one list", P1): ONE entry per
// service, joined on the server from the four records a service can have
// (a connection, a data source, a REST API and an MCP server from before;
// backend services/connections/directory.py, routes/connections_directory.py).
// Read-only here: the pickers (the designer, a workflow's Every step can
// use and its canvas, the job form, a run's inputs) read it to offer one
// row per service.
//
// `null` = no directory to read: the workspace is not on Connections (409
// `connections_disabled`) or the api has it switched off (404
// `directory_off`, LATTICE_CONNECTIONS_DIRECTORY=0) or does not have it yet
// (404). The caller then builds its rows as before (vendorModel.vendorsOf
// over connectionRows.buildRows).
//
// Its key sits under ["connections", customerId], so every connections
// mutation (connect, remove, re-scope) refreshes it too.

import { useQuery, type QueryClient } from "@tanstack/react-query";

import { ApiError, errorCode } from "@/lib/api";
import type { components } from "@/types/api.gen";

import { directoryKey, fetchDirectory, type DirectoryRead } from "./connectionDirectory";

export type { ConnectionDirectory, DirectoryEntry, DirectoryMember } from "./connectionDirectory";
export type DirectoryAbilities = components["schemas"]["DirectoryAbilities"];
export { directoryKey };

/** A 404 (switched off, or an api without it) or 409 `connections_disabled`: no directory, build the rows as before. */
export function directoryAbsent(e: unknown): boolean {
  if (!(e instanceof ApiError)) return false;
  if (e.status === 404) return true;
  return e.status === 409 && errorCode(e) === "connections_disabled";
}

/**
 * The directory, or null without one. `customerId` null = not asked (a
 * workspace known to be off Connections need not ask). Shares its key and
 * its one read with Settings' `useConnectionDirectory` (the cache holds a
 * DirectoryRead; this selects the list from it).
 */
export function useConnectionsDirectory(customerId: string | null) {
  return useQuery({
    queryKey: directoryKey(customerId),
    enabled: !!customerId,
    queryFn: ({ signal }) => fetchDirectory(customerId!, signal),
    select: (r: DirectoryRead) => (r.state === "on" ? r.directory : null),
    staleTime: 10_000,
    // An agent can connect something mid-conversation; a source being looked into settles in seconds.
    refetchInterval: (q) => {
      const d = q.state.data;
      if (d?.state !== "on") return false;
      return (d.directory.entries ?? []).some((e) => e.status === "discovering") ? 3_000 : 30_000;
    },
  });
}

/** After something was connected here (a data source too, which the connections key does not cover). */
export function refreshDirectory(qc: QueryClient, customerId: string): Promise<void> {
  return qc.invalidateQueries({ queryKey: directoryKey(customerId) });
}
